// Application notification policy. Sonner alone owns DOM presence and exit cleanup.
import type React from "react";

export type ToastTone = "info" | "success" | "warning" | "danger";
export const TOAST_DEFAULT_MS = 9_000;
export const TOAST_ACTION_MS = 18_000;
export const TOAST_STACK_CAP = 3;
export interface ToastAction { label: string; run: () => void | Promise<void> }
export interface ToastSpec {
  tone?: ToastTone;
  title?: string;
  msg?: React.ReactNode;
  mergeKey?: string;
  open?: ToastAction;
  undo?: ToastAction;
  durationMs?: number;
}
export type PushToast = (toast: ToastSpec) => void;
export type ToastInteractionSource = "hover" | "focus";
interface RecordEntry {
  spec: ToastSpec;
  expiresAt: number;
  remainingMs: number;
  hover: boolean;
  focus: boolean;
}
export interface NotificationSink {
  show: (id: string) => void;
  dismiss: (id: string) => void;
}
export function toastDurationMs(spec: ToastSpec): number {
  return typeof spec.durationMs === "number" ? spec.durationMs
    : spec.open || spec.undo ? TOAST_ACTION_MS : TOAST_DEFAULT_MS;
}
let nextScope = 0;

/** One disposable scope; callbacks handed to screens can never address a later scope. */
export class NotificationPolicy {
  private readonly prefix = `reqventos-notification-${++nextScope}`;
  private sequence = 0;
  private disposed = false;
  private records = new Map<string, RecordEntry>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private listeners = new Set<() => void>();
  constructor(private sink: NotificationSink) {}
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  isDisposed = () => this.disposed;
  get = (id: string) => this.records.get(id)?.spec;
  hasMergeKey = (key: string) => [...this.records.values()].some((entry) => entry.spec.mergeKey === key);
  activeIds = () => [...this.records.keys()];
  push: PushToast = (spec) => {
    if (this.disposed) return;
    if (spec.mergeKey) {
      for (const [id, entry] of this.records) {
        if (entry.spec.mergeKey === spec.mergeKey) this.dismiss(id);
      }
    }
    const id = `${this.prefix}-${++this.sequence}`;
    const duration = toastDurationMs(spec);
    this.records.set(id, { spec, expiresAt: Date.now() + duration, remainingMs: duration, hover: false, focus: false });
    if (this.records.size > TOAST_STACK_CAP) {
      const victim = [...this.records].find(([, entry]) => !entry.hover && !entry.focus);
      if (victim) this.dismiss(victim[0]);
    }
    // If all existing cards are protected, the new one cannot take their place.
    if (this.records.has(id)) this.sink.show(id);
    this.schedule();
  };
  dismiss = (id: string) => {
    if (!this.records.delete(id)) return;
    this.changed(); // Lock actions synchronously, before Sonner begins its exit.
    this.sink.dismiss(id);
    this.schedule();
  };
  act = (id: string, action: "open" | "undo") => {
    const run = this.records.get(id)?.spec[action]?.run;
    if (!run || this.disposed) return;
    this.dismiss(id);
    void run();
  };
  interact = (id: string, source: ToastInteractionSource, active: boolean) => {
    const entry = this.records.get(id);
    if (!entry || (!entry.spec.open && !entry.spec.undo) || entry[source] === active) return;
    const paused = entry.hover || entry.focus;
    if (!paused && active) entry.remainingMs = Math.max(0, entry.expiresAt - Date.now());
    entry[source] = active;
    if (paused && !entry.hover && !entry.focus) entry.expiresAt = Date.now() + entry.remainingMs;
    this.schedule();
  };
  dispose = () => {
    this.disposed = true;
    const ids = this.activeIds();
    this.records.clear(); // Release all callbacks and merge metadata, including pending async producers.
    clearTimeout(this.timer);
    this.timer = undefined;
    this.changed();
    ids.forEach(this.sink.dismiss);
  };
  private changed() { this.listeners.forEach((listener) => listener()); }
  private schedule() {
    clearTimeout(this.timer);
    this.timer = undefined;
    let next = Infinity;
    for (const entry of this.records.values()) {
      if (!entry.hover && !entry.focus) next = Math.min(next, entry.expiresAt);
    }
    if (!Number.isFinite(next) || this.disposed) return;
    this.timer = setTimeout(() => {
      const now = Date.now();
      for (const [id, entry] of this.records) {
        if (!entry.hover && !entry.focus && entry.expiresAt <= now) this.dismiss(id);
      }
      this.schedule();
    }, Math.max(0, next - Date.now()));
  }
}
